/**
 * clientNewProfileRouting.js
 *
 * Sub-fase 5.3.1 · Skeleton router · helpers PUROS de decisión de guardado
 * para ClientNewProfile.jsx.
 *
 * ⚠️ ESTE MÓDULO NO SE INVOCA TODAVÍA DESDE handleGlobalSave.
 *    Sólo se ejercita por tests unitarios. La conexión al guardado real
 *    se hará en Sub-fase 5.3.2 con OK explícito de Ann.
 *
 * Sin imports de React. Sin axios. 100% funciones puras.
 *
 * Whitelist conservadora aprobada por Ann (DECISIÓN 5.3.1):
 *   - Sólo identidad, contacto, domicilio, tutor, SEPA básicos (iban/titular/
 *     dni/bic), pagador alternativo, anotaciones internas, fecha_alta,
 *     email_facturacion.
 *   - QUEDAN FUERA del PUT light (clasificados como sensibles · pendiente
 *     decisión Ann): servicio, es_paciente, activo_clinica, estado_clinica,
 *     sede_clinica, fecha_alta_clinica, fecha_baja_clinica, plataforma_*,
 *     rol_general, membresia_*, capabilities_*, sede, inscrito_clases_fijas,
 *     fecha_alta_clases, tipo_beca, facturacion_automatica, sepa_mandato_id,
 *     sepa_mandato_fecha, sepa_firmado, sepa_archivado.
 */

// ──────────────────────────────────────────────────────────────────────────
// Constantes
// ──────────────────────────────────────────────────────────────────────────

export const TIPOS_ACCION = Object.freeze({
  CAMBIAR_FORMA_COBRO: "CAMBIAR_FORMA_COBRO",
  CAMBIAR_CUOTA: "CAMBIAR_CUOTA",
  CAMBIAR_HORARIO: "CAMBIAR_HORARIO",
  CAMBIAR_SEDE: "CAMBIAR_SEDE",
  PUT_LIGHT: "PUT_LIGHT",
});

export const ENDPOINTS = Object.freeze({
  CAMBIAR_FORMA_COBRO: (id) =>
    `/admin/clients/${id}/clases-fijas/cambiar-forma-cobro`,
  CAMBIAR_CUOTA: (id) =>
    `/admin/clients/${id}/clases-fijas/cambiar-cuota`,
  CAMBIAR_HORARIO: (id) =>
    `/admin/clients/${id}/clases-fijas/cambiar-horario`,
  CAMBIAR_SEDE: (id) =>
    `/admin/clients/${id}/clases-fijas/cambiar-sede`,
  PUT_LIGHT: (id) => `/admin/clients/${id}`,
});

export const MOTIVOS_BLOQUEO = Object.freeze({
  CLIENTE_DE_BAJA:
    "CLIENTE_DE_BAJA",
  IS_ACTIVA_BLOQUEA_CAMBIO:
    "IS_ACTIVA_BLOQUEA_CAMBIO",
  // (legacy · se conserva para retrocompatibilidad con código previo, pero
  // el routing ahora deriva a CAMBIO_SEDE_REQUIERE_MODAL_DEDICADO)
  CAMBIO_SEDE_CLIENTE_INSCRITO_NO_SOPORTADO:
    "CAMBIO_SEDE_CLIENTE_INSCRITO_NO_SOPORTADO",
  // Nuevo (Ann 2026-05-20) · indica al UI que abra el modal dedicado.
  CAMBIO_SEDE_REQUIERE_MODAL_DEDICADO:
    "CAMBIO_SEDE_REQUIERE_MODAL_DEDICADO",
  DESINSCRIPCION_VIA_GUARDAR_NO_SOPORTADA:
    "DESINSCRIPCION_VIA_GUARDAR_NO_SOPORTADA",
  // Ann 2026-05-26 · cliente NO inscrito a clases fijas · prohibido alterar
  // forma_pago/cuota/turno_fijo desde "Guardar". El flujo de alta es el botón
  // verde "Inscribir a clases fijas" que abre ModalInscripcionClasesFijas.
  // Sin este bloqueo, el router llamaba a /clases-fijas/cambiar-cuota o
  // /cambiar-forma-cobro y el backend devolvía 422 SIN_SUSCRIPCION_ACTIVA
  // silencioso → toast inservible y botón "como muerto" (caso Montserrat
  // Vendrell Rius).
  CLIENTE_NO_INSCRITO_USE_BOTON_INSCRIPCION:
    "CLIENTE_NO_INSCRITO_USE_BOTON_INSCRIPCION",
  BECADO_FUERA_ALCANCE_UI:
    "BECADO_FUERA_ALCANCE_UI",
  TURNO_NO_COINCIDE_CUOTA_UI:
    "TURNO_NO_COINCIDE_CUOTA_UI",
});

// Whitelist conservadora aprobada (Ann · 5.3.1).
export const PUT_LIGHT_WHITELIST_CONSERVADORA = Object.freeze([
  // Identidad
  "nombre", "primer_apellido", "segundo_apellido",
  "email", "dni", "fecha_nacimiento", "sexo", "profesion",
  // «No facilita email» (Ann 03-11-2026)
  "sin_email", "sin_email_motivo",
  // Contacto
  "telefono", "telefono_secundario", "tiene_whatsapp", "tiene_telegram",
  // Domicilio
  "direccion", "numero", "piso", "codigo_postal",
  "ciudad", "provincia", "pais", "barrio",
  // Marketing/derivación
  "como_nos_conocio", "derivador_nombre", "derivador_id", "derivador_especialidad", "derivador_centro",
  // Tutor (menores)
  "tutor_nombre", "tutor_apellidos", "tutor_dni",
  "tutor_relacion", "tutor_telefono", "tutor_email",
  // SEPA · incluye firmado + mandato (FIX 2026-07-15, Ann): el checkbox
  // "Mandato SEPA firmado" estaba EXCLUIDO de esta whitelist → marcarlo daba
  // "No hay cambios que guardar" → nunca se guardaba → el mandato no se creaba
  // → SEPA no podía cobrar y el cliente quedaba invisible (sin cobro, sin deuda).
  "sepa_iban", "sepa_bic",
  "sepa_titular_cuenta", "sepa_titular_dni",
  "sepa_firmado", "sepa_mandato_id", "sepa_mandato_fecha",
  // Pagador alternativo
  "tiene_pagador_alternativo",
  "pagador_nombre", "pagador_apellidos", "pagador_dni",
  "pagador_relacion", "pagador_telefono", "pagador_email",
  "pagador_direccion", "pagador_piso", "pagador_codigo_postal",
  "pagador_ciudad", "pagador_provincia",
  // Anotaciones internas
  "observaciones", "comentarios_confidenciales", "notas_operativas",
  "cliente_especial", "es_monitorizado", "tarifa_familiar",
  // Email de facturación (sin facturacion_automatica)
  "email_facturacion",
  // Datos de facturación distintos (Ann 2026-07-28): bloque en la pestaña Datos
  // principales. Flag + tipo (empresa/tercero/adulto) + nombre/NIF + dirección
  // fiscal troceada. Los consume la facturación (Guardián); aquí solo se
  // persisten desde la ficha con el "Guardar" normal.
  "facturacion_datos_distintos", "facturacion_tipo",
  "facturacion_razon_social", "facturacion_nif", "facturacion_direccion_fiscal",
  "facturacion_direccion", "facturacion_numero", "facturacion_piso",
  "facturacion_codigo_postal", "facturacion_ciudad", "facturacion_provincia",
  "facturacion_pais",
  // Fecha de alta general (no fecha_alta_clases)
  "fecha_alta",
  // Membresía del canal (Ann OK 2026-07-05): decide qué vídeos ve el cliente.
  // Independiente de cuota/pago. NO incluye rol_general/capabilities (permisos).
  "membresia_id", "membresia_estado",
  // Casillas de acceso a plataformas (FIX 2026-07-23, Ann): estaban EXCLUIDAS →
  // desmarcar Canal/Gestor en la ficha se reportaba como "pendiente" y NUNCA se
  // guardaba → pacientes de fisio veían el Canal sin deberlo y no se podía apagar
  // a mano. Movidas aquí para que el desmarcado SÍ persista (igual que se hizo con
  // el mandato SEPA y la membresía). paciente_activa NO: tiene su propio endpoint.
  "plataforma_canal_activa", "plataforma_gestor_activa",
]);

// Campos que cambiarían en payload pero NO se incluyen en PUT light en 5.3.1.
// Se reportan en `pendientes` para que el admin los vea.
export const PUT_LIGHT_EXCLUIDOS_PENDIENTES = Object.freeze([
  "servicio", "es_paciente", "activo_clinica",
  "estado_clinica", "sede_clinica",
  "fecha_alta_clinica", "fecha_baja_clinica",
  // plataforma_canal_activa / plataforma_gestor_activa → movidas a la WHITELIST
  // (FIX 2026-07-23, Ann): ya se guardan al desmarcar en la ficha.
  "plataforma_paciente_activa",
  // membresia_id/membresia_estado movidos a la whitelist (Ann OK 2026-07-05):
  // el desplegable "Membresía del canal" ya se guarda en edición.
  "rol_general",
  "capabilities_extra", "capabilities_denied",
  "sede",
  "inscrito_clases_fijas",
  "fecha_alta_clases",
  "tipo_beca",
  "facturacion_automatica",
  "sepa_mandato_id", "sepa_mandato_fecha",
  "sepa_firmado", "sepa_archivado",
]);

export const CAMPOS_SEPA_BASICOS = Object.freeze([
  "sepa_iban", "sepa_bic",
  "sepa_titular_cuenta", "sepa_titular_dni",
]);

export const CAMPOS_PAGADOR = Object.freeze([
  "tiene_pagador_alternativo",
  "pagador_nombre", "pagador_apellidos", "pagador_dni",
  "pagador_relacion", "pagador_telefono", "pagador_email",
  "pagador_direccion", "pagador_piso", "pagador_codigo_postal",
  "pagador_ciudad", "pagador_provincia",
]);

// ──────────────────────────────────────────────────────────────────────────
// Helpers puros
// ──────────────────────────────────────────────────────────────────────────

/**
 * serializarTurno · normaliza y serializa canónicamente un turno_fijo
 * para comparación estable independiente del orden.
 *   Entrada: array de {dia, hora} (acepta undefined/null/[]).
 *   Salida: string JSON ordenado por (dia, hora).
 *
 * Mismo criterio que el backend usa internamente para detectar SIN_CAMBIO_REAL.
 */
export function serializarTurno(turno) {
  if (!turno || !Array.isArray(turno)) return "[]";
  const norm = turno
    .filter((t) => t && t.dia && t.hora)
    .map((t) => ({
      dia: String(t.dia).trim(),
      hora: String(t.hora).trim(),
    }))
    .sort((a, b) =>
      a.dia === b.dia
        ? a.hora.localeCompare(b.hora)
        : a.dia.localeCompare(b.dia),
    );
  return JSON.stringify(norm);
}

/**
 * detectarCambiosClasesFijas · compara originals (refs en carga) con current
 * (estado actual en UI) para los 3 campos críticos + flag de inscripción.
 *
 *   originals = {
 *     formaPago: string|null,
 *     cuotaId: string|null,
 *     turnoFijoSerializado: string,    // resultado de serializarTurno()
 *     inscritoClasesFijas: boolean,
 *   }
 *   current = {
 *     formaPago: string|null,
 *     cuotaId: string|null,
 *     turnoFijo: array,                // RAW · se serializa internamente
 *     inscritoClasesFijas: boolean,
 *   }
 *
 *   Devuelve { formaPagoCambia, cuotaCambia, turnoCambia, inscritoTrueAFalse }.
 *   Cualquier comparación tolera null/undefined.
 */
export function detectarCambiosClasesFijas(originals, current) {
  const o = originals || {};
  const c = current || {};
  const formaPagoCambia = (o.formaPago ?? null) !== (c.formaPago ?? null);
  const cuotaCambia = (o.cuotaId ?? null) !== (c.cuotaId ?? null);
  const turnoCambia =
    (o.turnoFijoSerializado ?? "[]") !== serializarTurno(c.turnoFijo);
  const inscritoTrueAFalse =
    o.inscritoClasesFijas === true && c.inscritoClasesFijas === false;
  return { formaPagoCambia, cuotaCambia, turnoCambia, inscritoTrueAFalse };
}

/**
 * detectarCambiosSepa · compara snapshot original SEPA básicos + pagador.
 *   originals = { sepa: {iban,bic,titular,dni}, pagador: {...} }
 *   current   = mismo formato
 *   Devuelve { sepaBasicoCambia, pagadorCambia, camposCambiados: [...] }.
 */
export function detectarCambiosSepa(originals, current) {
  const o = originals || {};
  const c = current || {};
  const camposCambiados = [];
  let sepaBasicoCambia = false;
  for (const k of CAMPOS_SEPA_BASICOS) {
    if ((o.sepa?.[k] ?? null) !== (c.sepa?.[k] ?? null)) {
      sepaBasicoCambia = true;
      camposCambiados.push(k);
    }
  }
  let pagadorCambia = false;
  for (const k of CAMPOS_PAGADOR) {
    if ((o.pagador?.[k] ?? null) !== (c.pagador?.[k] ?? null)) {
      pagadorCambia = true;
      camposCambiados.push(k);
    }
  }
  return { sepaBasicoCambia, pagadorCambia, camposCambiados };
}

/**
 * categorizarOperacionGuardado · clasifica los cambios sin decidir aún
 * el endpoint. Útil para diagnóstico/UI y como entrada de decidirRoutingGuardado.
 */
export function categorizarOperacionGuardado(originals, current, contextoCliente) {
  const cf = detectarCambiosClasesFijas(originals, current);
  const sepa = detectarCambiosSepa(originals, current);
  const personalCambia = !!current?.personalCambia;       // bool agregado por UI
  const sedeCambia = (originals?.sede ?? null) !== (current?.sede ?? null);
  const otrosPendientes = current?.otrosPendientes || []; // lista de keys excluidas
  const ctx = contextoCliente || {};
  return {
    formaPagoCambia: cf.formaPagoCambia,
    cuotaCambia: cf.cuotaCambia,
    turnoCambia: cf.turnoCambia,
    inscritoTrueAFalse: cf.inscritoTrueAFalse,
    sepaBasicoCambia: sepa.sepaBasicoCambia,
    pagadorCambia: sepa.pagadorCambia,
    sepaCamposCambiados: sepa.camposCambiados,
    personalCambia,
    sedeCambia,
    otrosPendientes,
    estado_clases: ctx.estado_clases || null,
    estado_cliente: ctx.estado_cliente || null,
    sus_estado: ctx.sus_estado || null,
    inscritoActual: !!current?.inscritoClasesFijas,
  };
}

/**
 * decidirRoutingGuardado · toma originals + current + contexto y decide:
 *   - lista ordenada de acciones (CAMBIAR_*, PUT_LIGHT)
 *   - si todo está bloqueado por una regla de negocio
 *   - lista de advertencias (no bloqueantes)
 *   - lista de pendientes (campos excluidos del PUT light en 5.3.1)
 *
 * Reglas de orden seguro (resumen § DISEÑO 5.3):
 *   - inscrito true→false  → bloqueo (debe usar baja oficial)
 *   - cliente baja/desactivado + cualquier op CF → bloqueo
 *   - estado_clases ∈ {incidencia_salud, pausado} o sus pausada_incidencia
 *     + cualquier op CF → bloqueo
 *   - cambio sede mientras inscrito → bloqueo
 *   - forma_pago='becado' (origen o destino) → bloqueo (BECADO_FUERA_ALCANCE_UI)
 *   - cuota+horario  → 1 sola CAMBIAR_CUOTA con nuevo_turno_fijo
 *   - FP + cuota[+horario] → CAMBIAR_FORMA_COBRO antes que CAMBIAR_CUOTA
 *   - FP + horario        → CAMBIAR_FORMA_COBRO antes que CAMBIAR_HORARIO
 *   - FP→domiciliacion + datos SEPA básicos cambian
 *       → PUT_LIGHT (sólo SEPA) ANTES que CAMBIAR_FORMA_COBRO
 *   - PUT_LIGHT (personales y SEPA no acoplados) al FINAL si quedan campos.
 *
 * Devuelve siempre la misma estructura (incluso bloqueado=true → acciones=[]).
 */
export function decidirRoutingGuardado(originals, current, contextoCliente) {
  const cat = categorizarOperacionGuardado(originals, current, contextoCliente);
  const advertencias = [];
  const pendientes = [...(cat.otrosPendientes || [])];
  const cliente_id = current?.cliente_id || originals?.cliente_id || null;

  const sinAcciones = (motivo) => ({
    acciones: [],
    bloqueado: true,
    motivo_bloqueo: motivo,
    advertencias,
    pendientes,
  });

  // ────────── Bloqueos duros (orden estricto) ──────────

  // 1. Desinscripción true→false vía Guardar
  if (cat.inscritoTrueAFalse) {
    return sinAcciones(MOTIVOS_BLOQUEO.DESINSCRIPCION_VIA_GUARDAR_NO_SOPORTADA);
  }

  // Si NO hay cambios CF/sede ni personal ni sepa, pasamos a "sin acciones" sin bloqueo.
  const hayCambioCF =
    cat.formaPagoCambia || cat.cuotaCambia || cat.turnoCambia;
  const hayCambioSepaBasico = cat.sepaBasicoCambia || cat.pagadorCambia;
  const hayCambioPersonal = cat.personalCambia;
  const hayCambioSede = cat.sedeCambia;

  // 2+3. Cliente de baja/desactivado o en Incidencia de Salud (IS).
  //
  // Regla de negocio: NO se permite MODIFICAR Clases Fijas (forma de pago,
  // cuota, turno) mientras el cliente está de baja o en IS. PERO los datos
  // personales y, en particular, las OBSERVACIONES deben poder guardarse
  // SIEMPRE (Ann 2026-06-23).
  //
  // Antes esto hacía `return sinAcciones(...)` y abortaba el guardado ENTERO:
  // bastaba un "cambio CF fantasma" detectado al cargar la ficha de un cliente
  // de baja (turno/cuota residual que no serializa idéntico) para que ni
  // siquiera se pudiera escribir un comentario (síntoma reportado: Susana
  // Miranda sí bloqueada, Teresa Marti no · "sin lógica aparente").
  //
  // Nuevo comportamiento:
  //   - Si SOLO se intentaba tocar Clases Fijas (sin cambios personales/SEPA)
  //     → se mantiene el bloqueo duro de antes (mismo motivo y toast).
  //   - Si además hay cambios guardables (personales/observaciones o SEPA)
  //     → se DESCARTAN solo las acciones CF y se guarda el resto, dejando una
  //       advertencia no bloqueante.
  const deBaja =
    String(cat.estado_cliente || "").toLowerCase() === "desactivado" ||
    String(cat.estado_clases || "").toLowerCase() === "baja";
  const enIS =
    ["incidencia_salud", "pausado"].includes(
      String(cat.estado_clases || "").toLowerCase()) ||
    String(cat.sus_estado || "").toLowerCase() === "pausada_incidencia";

  let ignorarCFporEstado = false;
  if (hayCambioCF && (deBaja || enIS)) {
    const motivoEstado = deBaja
      ? MOTIVOS_BLOQUEO.CLIENTE_DE_BAJA
      : MOTIVOS_BLOQUEO.IS_ACTIVA_BLOQUEA_CAMBIO;
    const hayOtrosCambiosGuardables = hayCambioPersonal || hayCambioSepaBasico;
    if (!hayOtrosCambiosGuardables) {
      // Solo se intentaba modificar Clases Fijas → bloqueo duro (sin cambios).
      return sinAcciones(motivoEstado);
    }
    // Hay datos personales/observaciones (o SEPA) que SÍ deben guardarse:
    // descartamos únicamente las acciones CF y seguimos con el resto.
    ignorarCFporEstado = true;
    cat.formaPagoCambia = false;
    cat.cuotaCambia = false;
    cat.turnoCambia = false;
    advertencias.push({ code: motivoEstado, detail: { cf_descartado: true } });
  }

  // 3-bis. MEMBRESÍA SOLO-ONLINE (club / club_friend) · Ann 2026-07-28/29: una socia
  // solo-online (Club de Stripe O becada club_friend) NO tiene clases fijas → Ann debe
  // poder EDITAR su ficha LIBREMENTE (p.ej. APAGAR el Canal a una que NO paga) sin que
  // el guardado le exija "Inscribir a clases fijas". "Forma de pago" y "cuota"
  // (conceptos de clases fijas) NO aplican a un online → se DESCARTAN esas acciones CF
  // y se guarda el resto (plataformas, membresía, datos personales…).
  // AMPLIADO de solo club_friend a TAMBIÉN club (Ann 2026-07-29 · "los abusones fuera":
  // el CRM no dejaba apagar el Canal a un Club que no paga). NO fuerza ningún cambio de
  // membresía ni cobro: solo deja de bloquear el guardado por falta de clases fijas.
  const membresiaOnline = String(current?.membresia_id || "").toLowerCase();
  const esSoloOnline =
    membresiaOnline === "club" || membresiaOnline === "club_friend";
  if (hayCambioCF && esSoloOnline && !ignorarCFporEstado) {
    ignorarCFporEstado = true;
    cat.formaPagoCambia = false;
    cat.cuotaCambia = false;
    cat.turnoCambia = false;
    advertencias.push({
      code: "MEMBRESIA_ONLINE_SOLO_SIN_CLASES_FIJAS",
      detail: { cf_descartado: true, membresia: membresiaOnline },
    });
  }

  // 4. Cambio de sede con cliente inscrito · requiere modal dedicado
  //    "Cambiar sede de clases fijas" (autopista oficial, Ann 2026-05-20).
  //    Devuelve motivo específico para que el UI abra el modal en vez del
  //    toast de error legacy.
  if (hayCambioSede && cat.inscritoActual === true) {
    return sinAcciones(MOTIVOS_BLOQUEO.CAMBIO_SEDE_REQUIERE_MODAL_DEDICADO);
  }

  // 5/5-bis. PR-BECA-2 (Ann 2026-05-27): bloqueos `BECADO_FUERA_ALCANCE_UI`
  // retirados. La autopista oficial permite entrar y salir de becado vía
  // los endpoints atómicos cambiar_membresia + cambiar_forma_cobro, con
  // orden condicional (ver § "Construir secuencia de acciones" más abajo)
  // y defensas asimétricas backend (defensa A en cambiar_forma_cobro
  // cuando nueva='becado'; defensa B en cambiar_membresia cuando se sale
  // de cuota becada).

  // 5-ter. Cliente NO inscrito a clases fijas + intento de cambio CF.
  // Los endpoints /clases-fijas/cambiar-{forma-cobro,cuota,horario} exigen
  // suscripción activa (devuelven 422 SIN_SUSCRIPCION_ACTIVA cuando no la
  // hay). El alta de clases fijas es un flujo distinto: botón verde
  // "Inscribir a clases fijas" → POST /admin/clients/{id}/inscripcion-clases-fijas.
  // Sin este bloqueo el toast de error backend quedaba opaco y el botón
  // parecía "muerto" (caso Montserrat Vendrell Rius, Ann 2026-05-26).
  if (!ignorarCFporEstado && cat.inscritoActual === false && hayCambioCF) {
    return sinAcciones(
      MOTIVOS_BLOQUEO.CLIENTE_NO_INSCRITO_USE_BOTON_INSCRIPCION);
  }

  // ────────── Construir secuencia de acciones ──────────

  const acciones = [];

  // 6. Si FP cambia a 'domiciliacion' Y se modifican datos SEPA básicos, primero
  // PUT light (sólo SEPA básicos + pagador) para que cuando llegue a
  // cambiar-forma-cobro estén persistidos en BD.
  const fpDestino = String(current?.formaPago || "").toLowerCase();
  const fpCambiaADomiciliacion =
    cat.formaPagoCambia && fpDestino === "domiciliacion";
  const ponerPutSepaAntesDeFp =
    fpCambiaADomiciliacion && hayCambioSepaBasico;

  if (ponerPutSepaAntesDeFp) {
    acciones.push({
      tipo: TIPOS_ACCION.PUT_LIGHT,
      endpoint: cliente_id ? ENDPOINTS.PUT_LIGHT(cliente_id) : null,
      payloadHint: {
        scope: "sepa_pre_fp",
        camposIncluidos: cat.sepaCamposCambiados,
      },
    });
  }

  // 7+8. Cambio de FP + Cuota (con orden condicional para flujos de beca).
  //
  // PR-BECA-2 (Ann 2026-05-27): la dirección de la transición decide el
  // orden de las acciones para que un fallo intermedio NUNCA deje al
  // cliente en estado peligroso `forma_pago=becado + cuota_normal` (genera
  // cobros normales que SEPA excluye → deuda no remesable).
  //
  //   • ENTRAR EN BECADO (cuota destino es_becado=true):
  //       CAMBIAR_CUOTA primero, CAMBIAR_FORMA_COBRO después.
  //       Si falla el 2.º paso → cuota becada + forma_pago anterior.
  //       Estado intermedio NO peligroso: la cuota becada impide cobros.
  //
  //   • SALIR DE BECADO (cuota origen es_becado=true · destino normal):
  //       CAMBIAR_FORMA_COBRO primero, CAMBIAR_CUOTA después.
  //       Si falla el 2.º paso → forma_pago normal + cuota becada.
  //       Estado intermedio NO peligroso: la cuota becada sigue impidiendo
  //       cobros mientras la admin reintenta.
  //
  //   • RESTO DE CASOS (sin beca): orden histórico FP → CUOTA.
  const cuotaOrigenEsBecada = current?.cuotaActualEsBecada === true;
  const cuotaDestinoEsBecada = current?.cuotaNuevaEsBecada === true;
  const transicionEntradaBecado = cat.cuotaCambia && cuotaDestinoEsBecada;
  const transicionSalidaBecado =
    cat.cuotaCambia && cuotaOrigenEsBecada && !cuotaDestinoEsBecada;

  const accionFP = cat.formaPagoCambia ? {
    tipo: TIPOS_ACCION.CAMBIAR_FORMA_COBRO,
    endpoint: cliente_id ? ENDPOINTS.CAMBIAR_FORMA_COBRO(cliente_id) : null,
    payloadHint: { nueva_forma_pago: current?.formaPago ?? null },
  } : null;

  let accionCuota = null;
  let accionHorario = null;
  if (cat.cuotaCambia) {
    const payloadHint = { nueva_cuota_id: current?.cuotaId ?? null };
    if (cat.turnoCambia) {
      payloadHint.nuevo_turno_fijo = current?.turnoFijo ?? [];
    }
    accionCuota = {
      tipo: TIPOS_ACCION.CAMBIAR_CUOTA,
      endpoint: cliente_id ? ENDPOINTS.CAMBIAR_CUOTA(cliente_id) : null,
      payloadHint,
    };
  } else if (cat.turnoCambia) {
    accionHorario = {
      tipo: TIPOS_ACCION.CAMBIAR_HORARIO,
      endpoint: cliente_id ? ENDPOINTS.CAMBIAR_HORARIO(cliente_id) : null,
      payloadHint: { nuevo_turno_fijo: current?.turnoFijo ?? [] },
    };
  }

  if (transicionEntradaBecado) {
    // Entrada en becado: CUOTA primero, FP después.
    if (accionCuota) acciones.push(accionCuota);
    if (accionFP) acciones.push(accionFP);
  } else if (transicionSalidaBecado) {
    // Salida de becado: FP primero, CUOTA después.
    if (accionFP) acciones.push(accionFP);
    if (accionCuota) acciones.push(accionCuota);
  } else {
    // Resto: orden histórico FP → CUOTA.
    if (accionFP) acciones.push(accionFP);
    if (accionCuota) acciones.push(accionCuota);
  }
  if (accionHorario) acciones.push(accionHorario);

  // 9. PUT light final (personales o SEPA no acoplados a FP).
  // Si ya pusimos PUT_LIGHT pre-FP por SEPA, lo incluimos otra vez sólo si hay
  // OTROS cambios personales/sede pendientes. Si no hay nada más, no duplicar.
  const necesitaPutFinal =
    hayCambioPersonal ||
    (hayCambioSede && cat.inscritoActual !== true) ||
    (hayCambioSepaBasico && !ponerPutSepaAntesDeFp);
  if (necesitaPutFinal) {
    acciones.push({
      tipo: TIPOS_ACCION.PUT_LIGHT,
      endpoint: cliente_id ? ENDPOINTS.PUT_LIGHT(cliente_id) : null,
      payloadHint: {
        scope: "personal_y_sepa",
        // El builder real del payload viene en 5.3.2; aquí sólo señalamos
        // qué grupos van.
        incluyePersonales: hayCambioPersonal,
        incluyeSepaBasico: hayCambioSepaBasico && !ponerPutSepaAntesDeFp,
        incluyeSede: hayCambioSede && cat.inscritoActual !== true,
      },
    });
  }

  // 10. Advertencia · cuota cambia pero turno actual no coincide con nº turnos
  //     esperado (la UI debería ya haber forzado al admin a re-elegir turnos
  //     al cambiar cuota; aquí lo señalamos como warning, no bloqueamos).
  //
  // PR-BECA-2.1 (Ann 2026-05-27): la cuota becada NO impone número de turnos
  // (becado = importe 0€, conserva los turnos actuales). Por eso NO se emite
  // esta advertencia cuando la cuota destino es becada · sólo aplica a cuotas
  // normales.
  if (cat.cuotaCambia && current?.cuotaNuevaEsBecada !== true
      && current?.horasSemanaCuotaNueva != null
      && current?.turnoFijo
      && current.turnoFijo.length !== current.horasSemanaCuotaNueva) {
    advertencias.push({
      code: MOTIVOS_BLOQUEO.TURNO_NO_COINCIDE_CUOTA_UI,
      detail: {
        turnos_actuales: current.turnoFijo.length,
        horas_semana_cuota_nueva: current.horasSemanaCuotaNueva,
      },
    });
  }

  return {
    acciones,
    bloqueado: false,
    motivo_bloqueo: null,
    advertencias,
    pendientes,
  };
}

// ──────────────────────────────────────────────────────────────────────────
// Sub-fase 5.3.2-A · Motor de ejecución (preparado, NO conectado todavía)
// ──────────────────────────────────────────────────────────────────────────

/**
 * _aplanarParaWhitelist · helper interno · convierte el objeto current/originals
 * (con sepa/pagador anidados) en un mapa plano de claves de whitelist.
 * Sólo se exportan las claves que están en PUT_LIGHT_WHITELIST_CONSERVADORA.
 *
 * NO se exporta · es un detalle de implementación de construirPayloadPutLight.
 */
function _aplanarParaWhitelist(obj) {
  if (!obj || typeof obj !== "object") return {};
  const out = {};
  // Campos top-level que estén en la whitelist y NO sean objetos anidados
  for (const k of PUT_LIGHT_WHITELIST_CONSERVADORA) {
    if (k in obj) {
      const v = obj[k];
      // Sólo aceptar primitivas/null/undefined a top-level. Los objetos
      // anidados sepa/pagador se procesan abajo. Boolean/number/string ok.
      if (v === null || v === undefined
          || typeof v === "string" || typeof v === "number"
          || typeof v === "boolean") {
        out[k] = v;
      }
    }
  }
  // Campos SEPA básicos anidados en obj.sepa
  if (obj.sepa && typeof obj.sepa === "object") {
    for (const k of CAMPOS_SEPA_BASICOS) {
      if (k in obj.sepa) out[k] = obj.sepa[k];
    }
  }
  // Campos pagador anidados en obj.pagador
  if (obj.pagador && typeof obj.pagador === "object") {
    for (const k of CAMPOS_PAGADOR) {
      if (k in obj.pagador) out[k] = obj.pagador[k];
    }
  }
  return out;
}

/**
 * construirPayloadPutLight · construye el body para el endpoint PUT light
 * usando ÚNICAMENTE los campos que estén en PUT_LIGHT_WHITELIST_CONSERVADORA.
 *
 *   current   = mismo shape que el usado por el router (con sepa{}, pagador{})
 *   originals = mismo shape (refs originales)
 *   scope     = 'personal_y_sepa' (default) | 'sepa_pre_fp'
 *
 * Reglas:
 *   - Sólo claves de PUT_LIGHT_WHITELIST_CONSERVADORA (filtro duro · safety net).
 *   - Sólo el delta · si current[k] === originals[k] (con null≡undefined),
 *     no se incluye.
 *   - scope='sepa_pre_fp' · sólo SEPA básicos + pagador (subset de la whitelist).
 *   - NUNCA incluye: cuota_id, forma_pago, turno_fijo, sede,
 *     inscrito_clases_fijas, estado_clases, fecha_alta_clases, fecha_baja_clases,
 *     tipo_beca, facturacion_automatica, sepa_mandato_id, sepa_mandato_fecha,
 *     sepa_firmado, sepa_archivado, ni cualquier otra excluida en
 *     PUT_LIGHT_EXCLUIDOS_PENDIENTES (porque no están en la whitelist).
 *
 * Devuelve un objeto plano. {} si no hay diferencias.
 */
export function construirPayloadPutLight(current, originals, scope) {
  const flatCur = _aplanarParaWhitelist(current);
  const flatOrig = _aplanarParaWhitelist(originals);

  const efectiveScope = scope === "sepa_pre_fp" ? "sepa_pre_fp" : "personal_y_sepa";
  const clavesFuente = efectiveScope === "sepa_pre_fp"
    ? [...CAMPOS_SEPA_BASICOS, ...CAMPOS_PAGADOR]
    : [...PUT_LIGHT_WHITELIST_CONSERVADORA];

  const payload = {};
  for (const k of clavesFuente) {
    // Safety net duplicado · si alguien añade una clave fuera de la whitelist,
    // el filtro la elimina antes de salir por la red.
    if (!PUT_LIGHT_WHITELIST_CONSERVADORA.includes(k)) continue;
    const v = flatCur[k];
    const v0 = flatOrig[k];
    const eq = (v ?? null) === (v0 ?? null);
    if (!eq) payload[k] = v ?? null;
  }
  return payload;
}

/**
 * ejecutarSecuenciaDeAcciones · coordinador puro de ejecución serial estricta.
 * Recibe la decisión del router + un callback que ejecuta UNA acción.
 * Detiene la cadena al primer fallo, anota metadatos y lanza el error.
 *
 *   decision        = output de decidirRoutingGuardado()
 *   ejecutarAccionFn = async (accion) => respuesta · función inyectada por el
 *                      llamador para no acoplar el módulo a axios. En tests se
 *                      inyecta un mock.
 *
 * Devuelve:
 *   - éxito   → { ok: true, ejecutadas: [{accion, resp}, ...], saltado: false }
 *   - bloqueo o vacío → { ok: true, ejecutadas: [], saltado: true }
 *   - fallo   → throw err con metadatos:
 *       err._accionFallida      = accion que falló
 *       err._accionesEjecutadas = lista de las que sí pasaron, en orden
 *
 * Garantías:
 *   - Serial estricto (await en cada iteración).
 *   - Si la acción N falla, las acciones N+1..M NO se invocan.
 *   - El callback no debe modificar `decision.acciones` durante la ejecución
 *     (responsabilidad del llamador).
 */
export async function ejecutarSecuenciaDeAcciones(decision, ejecutarAccionFn) {
  if (typeof ejecutarAccionFn !== "function") {
    throw new TypeError(
      "ejecutarSecuenciaDeAcciones: ejecutarAccionFn debe ser función");
  }
  if (!decision || decision.bloqueado === true
      || !Array.isArray(decision.acciones)
      || decision.acciones.length === 0) {
    return { ok: true, ejecutadas: [], saltado: true };
  }
  const ejecutadas = [];
  for (const accion of decision.acciones) {
    try {
      const resp = await ejecutarAccionFn(accion);
      ejecutadas.push({ accion, resp });
    } catch (err) {
      const enriched = err instanceof Error ? err : new Error(String(err));
      enriched._accionFallida = accion;
      enriched._accionesEjecutadas = ejecutadas;
      throw enriched;
    }
  }
  return { ok: true, ejecutadas, saltado: false };
}
